import { SearchOutlined } from "@ant-design/icons";
import { Autocomplete, Circle, GoogleMap, Marker } from "@react-google-maps/api";
import { Input } from "antd";
import React, { useEffect, useRef, useState } from "react";
import { useMemo } from "react";
import { point } from "@turf/helpers";
import booleanPointInPolygon from "@turf/boolean-point-in-polygon";
import indonesiaGeoJSON from "../../../assets/indonesia.json";
import { showErrorMessage } from "../../../helpers/errorHandler";
interface Value {
	latitude: number;
	longitude: number;
}
interface MapsPickerComponentProps {
	customCenter?: {
		lng: number;
		lat: number;
	};
	initialZoom?: number;
	value?: Value;
	onChange?: (value: Value) => void;
	showSearch?: boolean;
	onMapsLoad?: (map: google.maps.Map) => void;
}

export function isPointInIndonesia(lat: number, lon: number): boolean {
	const featureCollection = indonesiaGeoJSON as any;
	const pt = point([lon, lat]);
	return featureCollection.features.some((feature: any) => booleanPointInPolygon(pt, feature));
}

const MapsPickerComponent: React.FC<MapsPickerComponentProps> = ({
	value,
	onChange,
	initialZoom,
	customCenter,
	showSearch,
	onMapsLoad,
}) => {
	const [center, setCenter] = useState<google.maps.LatLngLiteral>();
	const autocomplete = useRef<google.maps.places.Autocomplete>();
	const isFirstCenterRuned = useRef<boolean>(false);

	const markerCenter = useMemo(
		() =>
			value && value.latitude && value.longitude
				? {
						lat: value.latitude,
						lng: value.longitude,
				  }
				: undefined,
		[value]
	);

	useEffect(() => {
		if (isFirstCenterRuned.current) return;
		setCenter(
			customCenter
				? customCenter
				: markerCenter
				? markerCenter
				: {
						lng: 118.015776,
						lat: -2.6000285,
				  }
		);
		if (customCenter || markerCenter) {
			isFirstCenterRuned.current = true;
		}
		// eslint-disable-next-line
	}, [customCenter, markerCenter]);

	const _handleClick = (event: google.maps.MapMouseEvent) => {
		if (event.latLng) {
			const lat = event.latLng.lat();
			const lng = event.latLng.lng();

			if (isPointInIndonesia(lat, lng)) {
				onChange?.({
					latitude: lat,
					longitude: lng,
				});
			} else {
				showErrorMessage("Sorry, locations outside of Indonesia are not supported at this time.", true);
			}
		}
	};

	const _handlePlaceChange = () => {
		if (autocomplete.current) {
			let place = autocomplete.current.getPlace();
			let location = place.geometry?.location;
			if (location) {
				if (isPointInIndonesia(location.lat(), location.lng())) {
					setCenter({
						lat: location.lat(),
						lng: location.lng(),
					});
				} else {
					showErrorMessage("Sorry, locations outside of Indonesia are not supported at this time.", true);
				}
			}
		}
	};

	const onAutoCompleteLoaded = (value: google.maps.places.Autocomplete) => {
		autocomplete.current = value;
	};

	return (
		<GoogleMap
			onLoad={onMapsLoad}
			options={{
				gestureHandling: "cooperative",
			}}
			onClick={_handleClick}
			mapContainerStyle={{ width: "100%", height: 350, borderRadius: 10 }}
			center={center}
			zoom={initialZoom ? initialZoom : markerCenter ? 15 : 5}
		>
			{showSearch ? (
				<Autocomplete
					onLoad={onAutoCompleteLoaded}
					onPlaceChanged={_handlePlaceChange}
				>
					<Input
						suffix={<SearchOutlined />}
						style={{
							position: "absolute",
							left: "20px",
							top: "10px",
							maxWidth: "320px",
						}}
					/>
				</Autocomplete>
			) : (
				<></>
			)}
			{markerCenter ? (
				<React.Fragment>
					<Marker
						icon="/images/map-pin.svg"
						position={markerCenter}
						options={{
							draggable: onChange ? true : false,
						}}
						onDrag={_handleClick}
					/>
					<Circle
						center={markerCenter}
						radius={1000}
						visible={true}
						options={{
							fillColor: "#EA464680",
							strokeWeight: 0,
						}}
					/>
				</React.Fragment>
			) : (
				<></>
			)}
		</GoogleMap>
	);
};
export default MapsPickerComponent;
